"use client";

// ── netti's presence settings — the person's, kept in their browser ─────────
// Whether netti moves when it answers, and whether it makes its small sounds.
// Both default ON (the register is ambient — a presence, not a notification)
// and both are one tap to silence: in the rail, in the netti space, or by
// simply telling netti ("mute yourself" → set_preference). Stored per browser;
// a change is announced on the window bus so every mount agrees at once.

import * as React from "react";
import { setNettiSoundLevel } from "./sound";

export interface NettiPrefs {
  sounds: boolean;
  expressions: boolean;
  /** 0..1 — the ambient palette's ceiling. */
  volume: number;
}

export const NETTI_PREFS_KEY = "innernet:netti-prefs";
export const NETTI_PREFS_EVENT = "innernet:netti-prefs";

const DEFAULT: NettiPrefs = { sounds: true, expressions: true, volume: 0.7 };

export function loadNettiPrefs(): NettiPrefs {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const raw = localStorage.getItem(NETTI_PREFS_KEY);
    if (!raw) return DEFAULT;
    const p = JSON.parse(raw) as Partial<NettiPrefs>;
    return {
      sounds: typeof p.sounds === "boolean" ? p.sounds : DEFAULT.sounds,
      expressions: typeof p.expressions === "boolean" ? p.expressions : DEFAULT.expressions,
      volume: typeof p.volume === "number" ? Math.min(1, Math.max(0, p.volume)) : DEFAULT.volume,
    };
  } catch {
    return DEFAULT;
  }
}

/** Apply what the prefs mean to the audio graph (idempotent). */
export function applyNettiPrefs(p: NettiPrefs): void {
  setNettiSoundLevel(p.sounds ? p.volume : 0);
}

export function saveNettiPrefs(patch: Partial<NettiPrefs>): NettiPrefs {
  const next = { ...loadNettiPrefs(), ...patch };
  try { localStorage.setItem(NETTI_PREFS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  applyNettiPrefs(next);
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(NETTI_PREFS_EVENT, { detail: next }));
  return next;
}

/** The prefs as React state, shared across every mount via the window bus. */
export function useNettiPrefs(): [NettiPrefs, (patch: Partial<NettiPrefs>) => void] {
  const [prefs, setPrefs] = React.useState<NettiPrefs>(DEFAULT);
  React.useEffect(() => {
    const p = loadNettiPrefs();
    setPrefs(p);
    applyNettiPrefs(p);
    const on = (e: Event) => setPrefs((e as CustomEvent<NettiPrefs>).detail);
    window.addEventListener(NETTI_PREFS_EVENT, on);
    return () => window.removeEventListener(NETTI_PREFS_EVENT, on);
  }, []);
  const set = React.useCallback((patch: Partial<NettiPrefs>) => { setPrefs(saveNettiPrefs(patch)); }, []);
  return [prefs, set];
}
